import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, doc, getDoc, updateDoc, setDoc, query } from 'firebase/firestore';
import { 
  FaSearch, FaCommentDots, FaPlus, FaTimes, FaHashtag, 
  FaUserEdit, FaBirthdayCake, FaGift, FaUserCircle, FaUsers, FaCogs, FaCheckCircle, FaRocket
} from 'react-icons/fa';

// --- PREMIUM SAAS THEME ---
const THEME = {
  primary: '#4f46e5',    // Indigo
  primaryLight: '#e0e7ff',
  success: '#10b981',    // Emerald
  warning: '#f59e0b',    // Amber
  textMain: '#0f172a',   // Slate 900
  textSub: '#64748b',    // Slate 500
  border: '#e2e8f0',     // Slate 200
  bg: '#f8fafc',         // Slate 50
  card: '#ffffff'
};

const getAvatarColor = (phone) => {
  const colors = ['#ef4444', '#f97316', '#84cc16', '#0ea5e9', '#8b5cf6', '#d946ef', '#f43f5e'];
  let hash = 0;
  for (let i = 0; i < phone.length; i++) hash = phone.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// Helper: Check if a date is today or in the next 7 days (ignoring year)
const checkUpcoming = (dateStr) => {
  if(!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [year, month, day] = dateStr.split('-');
  let eventDate = new Date(today.getFullYear(), parseInt(month) - 1, parseInt(day));
  eventDate.setHours(0, 0, 0, 0);

  if (eventDate < today) eventDate.setFullYear(today.getFullYear() + 1);

  const diffDays = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24)); 
  return diffDays >= 0 && diffDays <= 7;
};

// Helper: Check if a date is EXACTLY today
const isToday = (dateStr) => {
  if(!dateStr) return false;
  const today = new Date();
  const [year, month, day] = dateStr.split('-');
  return today.getMonth() + 1 === parseInt(month) && today.getDate() === parseInt(day);
};

function SmartContacts() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [contacts, setContacts] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // States
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUpcoming, setFilterUpcoming] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); 
  
  // Profile Modal State
  const [editingContact, setEditingContact] = useState(null);
  const [profileForm, setProfileForm] = useState({ clientName: '', birthday: '', anniversary: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Automation Settings State
  const [showAutoSettings, setShowAutoSettings] = useState(false);
  const [occasionSettings, setOccasionSettings] = useState({ birthdayTemplate: '', anniversaryTemplate: '' });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        // Fetch Tags
        const tagsRef = doc(db, "businesses", u.uid, "settings", "crm_tags");
        const tagsSnap = await getDoc(tagsRef);
        if (tagsSnap.exists() && tagsSnap.data().list) setAvailableTags(tagsSnap.data().list);

        // Fetch Occasion Settings
        const occRef = doc(db, "businesses", u.uid, "settings", "occasions");
        const occSnap = await getDoc(occRef);
        if (occSnap.exists()) setOccasionSettings(occSnap.data());

        // Fetch Templates for Dropdown
        onSnapshot(query(collection(db, "businesses", u.uid, "templates")), (snapshot) => {
          setTemplates(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.status === "Approved"));
        });

        // Fetch Contacts
        onSnapshot(collection(db, "businesses", u.uid, "contacts"), (snapshot) => {
          const loadedContacts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          loadedContacts.sort((a, b) => (b.lastActive?.toMillis() || 0) - (a.lastActive?.toMillis() || 0));
          setContacts(loadedContacts);
        });
      }
    });
    return () => { window.removeEventListener('resize', handleResize); unsubscribe(); };
  }, []);

  // --- ACTIONS ---
  const handleAddTagToContact = async (contactId, currentTags, newTag) => {
    if (!user) return;
    const updatedTags = currentTags ? [...currentTags, newTag] : [newTag];
    try {
      await updateDoc(doc(db, "businesses", user.uid, "contacts", contactId), { tags: updatedTags });
      setActiveDropdown(null); 
    } catch (error) { console.error(error); }
  };

  const handleRemoveTagFromContact = async (contactId, currentTags, tagToRemove) => {
    if (!user) return;
    try { await updateDoc(doc(db, "businesses", user.uid, "contacts", contactId), { tags: currentTags.filter(t => t !== tagToRemove) }); } 
    catch (error) { console.error(error); }
  };

  const openEditModal = (contact) => {
    setProfileForm({ clientName: contact.clientName || '', birthday: contact.birthday || '', anniversary: contact.anniversary || '' });
    setEditingContact(contact);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!user || !editingContact) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, "businesses", user.uid, "contacts", editingContact.id), {
        clientName: profileForm.clientName, birthday: profileForm.birthday, anniversary: profileForm.anniversary
      });
      setEditingContact(null);
    } catch (error) { alert("Failed to save profile."); }
    setIsSaving(false);
  };

  const handleSaveAutoSettings = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await setDoc(doc(db, "businesses", user.uid, "settings", "occasions"), occasionSettings, { merge: true });
      setShowAutoSettings(false);
      alert("Auto-Sender Settings Saved! The backend will now process these daily at 8:00 AM.");
    } catch (error) { alert("Error saving settings."); }
    setIsSaving(false);
  };

  // --- FILTERS & MATH ---
  const upcomingBirthdaysCount = contacts.filter(c => checkUpcoming(c.birthday)).length;
  const totalTagsUsed = new Set(contacts.flatMap(c => c.tags || [])).size;

  const filteredContacts = contacts.filter(c => {
    const matchesSearch = c.phone.includes(searchQuery) || (c.clientName && c.clientName.toLowerCase().includes(searchQuery.toLowerCase())) || (c.tags && c.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())));
    if (filterUpcoming) return matchesSearch && (checkUpcoming(c.birthday) || checkUpcoming(c.anniversary));
    return matchesSearch;
  });

  if (!user) return <div style={{ padding: '40px', textAlign: 'center', fontWeight: 'bold', color: THEME.textSub }}>Loading CRM Engine...</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', padding: isMobile ? '15px' : '40px', background: THEME.bg, minHeight: '100vh' }}>
      
      {/* HEADER & SEARCH */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', marginBottom: '30px', gap: '20px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '900', color: THEME.textMain, margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaUsers color={THEME.primary}/> Smart Contacts
          </h1>
          <p style={{ margin: 0, color: THEME.textSub, fontSize: '15px' }}>Manage relationships, segments, and special occasions.</p>
        </div>
        
        <div style={{ position: 'relative', width: isMobile ? '100%' : '350px' }}>
          <FaSearch color={THEME.textSub} style={{ position: 'absolute', top: '14px', left: '16px' }} />
          <input 
            type="text" placeholder="Search names, phones, or tags..." 
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '12px 16px 12px 45px', borderRadius: '14px', border: `1px solid ${THEME.border}`, outline: 'none', boxSizing: 'border-box', fontSize: '14px', fontWeight: '500', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
          />
        </div>
      </div>

      {/* NEW: AUTOMATION SETTINGS BANNER */}
      <div style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', padding: '20px', borderRadius: '16px', marginBottom: '25px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '15px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)' }}>
        <div>
          <div style={{ color: 'white', fontSize: '18px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <FaRocket color={THEME.warning} /> Occasion Auto-Sender
          </div>
          <div style={{ color: '#a5b4fc', fontSize: '13px', fontWeight: '500' }}>Automatically send WhatsApp templates to clients on their exact Birthday or Anniversary.</div>
        </div>
        <button onClick={() => setShowAutoSettings(true)} style={{ background: 'white', color: '#312e81', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <FaCogs/> Configure Sender
        </button>
      </div>

      {/* QUICK STATS & FILTERS */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', overflowX: 'auto', paddingBottom: '10px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: `1px solid ${THEME.border}`, flex: 1, minWidth: '180px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', marginBottom: '8px' }}>Total Audience</div>
          <div style={{ fontSize: '32px', fontWeight: '900', color: THEME.textMain }}>{contacts.length}</div>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: `1px solid ${THEME.border}`, flex: 1, minWidth: '180px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', marginBottom: '8px' }}>Active Segments</div>
          <div style={{ fontSize: '32px', fontWeight: '900', color: THEME.primary }}>{totalTagsUsed}</div>
        </div>
        
        {/* CLICKABLE FILTER BOX */}
        <div 
          onClick={() => setFilterUpcoming(!filterUpcoming)}
          style={{ background: filterUpcoming ? '#fdf2f8' : 'white', padding: '20px', borderRadius: '16px', border: filterUpcoming ? `2px solid #db2777` : `1px solid ${THEME.border}`, flex: 1, minWidth: '200px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', cursor: 'pointer', transition: 'all 0.2s', position: 'relative' }}
        >
          {filterUpcoming && <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#db2777', color: 'white', fontSize: '10px', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>FILTER ACTIVE</div>}
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#db2777', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FaBirthdayCake/> Upcoming (7 Days)
          </div>
          <div style={{ fontSize: '32px', fontWeight: '900', color: '#be185d' }}>{upcomingBirthdaysCount}</div>
        </div>
      </div>

      {/* CONTACTS TABLE */}
      <div style={{ background: 'white', borderRadius: '20px', border: `1px solid ${THEME.border}`, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)', overflow: 'visible' }}>
        <div style={{ overflowX: 'auto', paddingBottom: '100px' }}> 
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '900px' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${THEME.border}`, background: '#f8fafc' }}>
                <th style={{ padding: '18px 25px', fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Client Profile</th>
                <th style={{ padding: '18px 25px', fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Segments / Tags</th>
                <th style={{ padding: '18px 25px', fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Occasions</th>
                <th style={{ padding: '18px 25px', fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent Activity</th>
                <th style={{ padding: '18px 25px', fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: THEME.textSub }}>
                    <FaUserCircle size={40} style={{ opacity: 0.2, marginBottom: '15px' }}/>
                    <div style={{ fontWeight: '600', fontSize: '15px' }}>No contacts found matching your criteria.</div>
                  </td>
                </tr>
              ) : (
                filteredContacts.map(contact => (
                  <tr key={contact.id} style={{ borderBottom: `1px solid ${THEME.border}`, transition: 'background 0.2s', ':hover': {background: '#f8fafc'}, background: isToday(contact.birthday) ? '#fdf2f8' : 'transparent' }}>
                    
                    <td style={{ padding: '15px 25px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ width: '45px', height: '45px', borderRadius: '50%', background: getAvatarColor(contact.phone), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          {contact.clientName ? contact.clientName.substring(0, 2).toUpperCase() : contact.phone.substring(0, 2)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', color: THEME.textMain, fontSize: '15px' }}>{contact.clientName || 'Unknown Client'}</div>
                          <div style={{ fontWeight: '600', color: THEME.textSub, fontSize: '12px', marginTop: '2px' }}>+{contact.phone}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td style={{ padding: '15px 25px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        {contact.tags && contact.tags.map(tag => (
                          <span key={tag} style={{ background: '#f0fdf4', color: '#047857', border: '1px solid #bbf7d0', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                            <FaHashtag size={10} style={{opacity: 0.5}}/> {tag}
                            <FaTimes style={{ cursor: 'pointer', opacity: 0.5, ':hover': {opacity: 1} }} onClick={() => handleRemoveTagFromContact(contact.id, contact.tags, tag)}/>
                          </span>
                        ))}
                        <div style={{ position: 'relative' }}>
                          <button onClick={() => setActiveDropdown(activeDropdown === contact.id ? null : contact.id)} style={{ background: 'white', border: `1px dashed #94a3b8`, color: '#64748b', padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.2s' }}>
                            <FaPlus size={10}/> Tag
                          </button>
                          {activeDropdown === contact.id && (
                            <>
                              <div style={{ position: 'fixed', inset: 0, zIndex: 90 }} onClick={() => setActiveDropdown(null)}></div>
                              <div style={{ position: 'absolute', top: '100%', left: '0', marginTop: '8px', background: 'white', border: `1px solid ${THEME.border}`, borderRadius: '12px', boxShadow: '0 15px 30px -5px rgba(0,0,0,0.15)', width: '200px', zIndex: 100, padding: '8px', animation: 'fadeIn 0.2s' }}>
                                {availableTags.length === 0 ? (
                                  <div style={{ padding: '15px 10px', fontSize: '12px', color: THEME.textSub, textAlign: 'center', fontWeight: '500' }}>Create tags in the Connect API tab first.</div>
                                ) : (
                                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: THEME.textSub, padding: '5px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Available Tags</div>
                                    {availableTags.filter(t => !(contact.tags || []).includes(t)).map(tag => (
                                      <div key={tag} onClick={() => handleAddTagToContact(contact.id, contact.tags, tag)} style={{ padding: '10px', fontSize: '13px', fontWeight: '700', color: THEME.textMain, cursor: 'pointer', borderRadius: '8px', transition: 'background 0.1s', display: 'flex', alignItems: 'center', gap: '8px' }} onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}><FaHashtag color={THEME.primary} size={12}/> {tag}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '15px 25px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {contact.birthday ? (
                          <span style={{ fontSize: '12px', fontWeight: '800', color: isToday(contact.birthday) ? '#be185d' : '#db2777', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FaBirthdayCake/> {contact.birthday.substring(5)}
                            {isToday(contact.birthday) && <span style={{ background: '#be185d', color: 'white', fontSize: '9px', padding: '2px 6px', borderRadius: '10px' }}>TODAY</span>}
                          </span>
                        ) : (<span style={{ fontSize: '11px', color: '#cbd5e1', fontStyle: 'italic' }}>No birthday</span>)}
                        
                        {contact.anniversary ? (
                          <span style={{ fontSize: '12px', fontWeight: '800', color: '#0284c7', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FaGift/> {contact.anniversary.substring(5)}
                          </span>
                        ) : (<span style={{ fontSize: '11px', color: '#cbd5e1', fontStyle: 'italic' }}>No anniversary</span>)}
                      </div>
                    </td>
                    
                    <td style={{ padding: '15px 25px' }}>
                      <div style={{ fontSize: '13px', color: THEME.textMain, fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{contact.lastMessage || 'No messages yet.'}</div>
                      <div style={{ fontSize: '11px', color: THEME.textSub, marginTop: '4px', fontWeight: '600' }}>{contact.lastActive?.toDate ? contact.lastActive.toDate().toLocaleDateString([], {month:'short', day:'numeric'}) : ''}</div>
                    </td>
                    
                    <td style={{ padding: '15px 25px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button onClick={() => openEditModal(contact)} style={{ background: 'white', color: THEME.primary, border: `1px solid ${THEME.border}`, width: '36px', height: '36px', borderRadius: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }} title="Edit Profile"><FaUserEdit size={16} /></button>
                        <button style={{ background: '#ecfdf5', color: THEME.success, border: 'none', width: '36px', height: '36px', borderRadius: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(16,185,129,0.1)' }} title="Go to Chat"><FaCommentDots size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- NEW: AUTO-SENDER SETTINGS MODAL --- */}
      {showAutoSettings && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(5px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', width: '100%', maxWidth: '500px', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s' }}>
            <div style={{ background: '#f8fafc', padding: '25px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: '900', color: THEME.textMain }}>Occasion Auto-Sender</h3>
                <div style={{ fontSize: '13px', color: THEME.textSub, fontWeight: '600' }}>Fires daily at 8:00 AM automatically.</div>
              </div>
              <button onClick={() => setShowAutoSettings(false)} style={{ border: 'none', background: 'white', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.textSub, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}><FaTimes size={16}/></button>
            </div>

            <form onSubmit={handleSaveAutoSettings} style={{ padding: '30px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: '#db2777', marginBottom: '8px' }}><FaBirthdayCake/> Birthday Template</label>
                <select value={occasionSettings.birthdayTemplate} onChange={e => setOccasionSettings({...occasionSettings, birthdayTemplate: e.target.value})} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', fontWeight: '600' }}>
                  <option value="">-- Disabled (Do not send) --</option>
                  {templates.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: '#0284c7', marginBottom: '8px' }}><FaGift/> Anniversary Template</label>
                <select value={occasionSettings.anniversaryTemplate} onChange={e => setOccasionSettings({...occasionSettings, anniversaryTemplate: e.target.value})} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', fontWeight: '600' }}>
                  <option value="">-- Disabled (Do not send) --</option>
                  {templates.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <button type="button" onClick={() => setShowAutoSettings(false)} style={{ flex: 1, padding: '16px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '800', color: THEME.textSub, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={isSaving} style={{ flex: 2, padding: '16px', background: '#312e81', border: 'none', borderRadius: '12px', fontWeight: '800', color: 'white', cursor: isSaving ? 'wait' : 'pointer', boxShadow: '0 4px 15px rgba(49, 46, 129, 0.3)' }}>
                  {isSaving ? 'Saving...' : 'Save Activations'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- PROFILE EDIT MODAL --- */}
      {editingContact && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(5px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', width: '100%', maxWidth: '450px', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s' }}>
            <div style={{ background: '#f8fafc', padding: '25px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: '900', color: THEME.textMain }}>Client Profile</h3>
                <div style={{ fontSize: '13px', color: THEME.textSub, fontWeight: '600' }}>+{editingContact.phone}</div>
              </div>
              <button onClick={() => setEditingContact(null)} style={{ border: 'none', background: 'white', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.textSub, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}><FaTimes size={16}/></button>
            </div>

            <form onSubmit={handleSaveProfile} style={{ padding: '30px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: THEME.textSub, marginBottom: '8px' }}>Full Name</label>
                <input type="text" placeholder="e.g. Aniket Singh" value={profileForm.clientName} onChange={e => setProfileForm({...profileForm, clientName: e.target.value})} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '15px', fontWeight: '600', boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: '#db2777', marginBottom: '8px' }}><FaBirthdayCake/> Birthday</label>
                  <input type="date" value={profileForm.birthday} onChange={e => setProfileForm({...profileForm, birthday: e.target.value})} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', fontWeight: '600', boxSizing: 'border-box', color: THEME.textMain }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: '#0284c7', marginBottom: '8px' }}><FaGift/> Anniversary</label>
                  <input type="date" value={profileForm.anniversary} onChange={e => setProfileForm({...profileForm, anniversary: e.target.value})} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', fontWeight: '600', boxSizing: 'border-box', color: THEME.textMain }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <button type="button" onClick={() => setEditingContact(null)} style={{ flex: 1, padding: '16px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '800', color: THEME.textSub, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={isSaving} style={{ flex: 2, padding: '16px', background: THEME.primary, border: 'none', borderRadius: '12px', fontWeight: '800', color: 'white', cursor: isSaving ? 'wait' : 'pointer', boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)' }}>
                  {isSaving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SmartContacts;